import { useState, type ReactNode } from 'react';

import { ConflictNotice, type ConflictRow } from '../components/ConflictNotice';
import { isVersionConflict, planVersionConflict } from './version-conflict';

/**
 * One form's answer to a 409 `version_conflict` (lib/version-conflict.ts).
 *
 * The screen hands its save's error and the two drafts it knows: what the
 * form started from (`base`) and what was sent (`mine`). The form is then
 * rebased on what is saved now: it shows the saved record with this person's
 * changes on top, and the saved record becomes its new starting point.
 *
 * - Different fields changed: the rebased form is saved right away with the
 *   fresh version, and a quiet line says both changes stayed.
 * - The same field changed: nothing is sent. The notice shows each clash and
 *   the form keeps what was typed until the person picks "Guardar lo mío"
 *   (save what the form holds now) or "Usar lo guardado".
 *
 * Nothing the other person saved is reverted by "Guardar lo mío": fields only
 * they changed are already in the rebased form.
 */
export function useVersionConflict<SavedRecord, Draft extends Record<string, unknown>>(options: {
  keys: readonly (keyof Draft & string)[];
  labels: Partial<Record<keyof Draft & string, string>>;
  format?: Partial<{ [Key in keyof Draft & string]: (value: Draft[Key]) => string }>;
  /** The 409 body's record, parsed with the same schema as a read; null when it does not parse. */
  parse: (current: unknown) => SavedRecord | null;
  toDraft: (record: SavedRecord) => Draft;
  /** Put `draft` in the form and make `record` (and its version) the form's new starting point. */
  rebase: (input: { draft: Draft; record: SavedRecord }) => void;
  /** Save `draft`, naming `version` as the one it was read at. */
  save: (input: { draft: Draft; version: number }) => void;
  /** What the form holds right now; null when it does not validate (the form shows why). */
  current: () => Draft | null;
  /** "Usar lo guardado": the form takes the saved record and lets this person's changes go. */
  discard: (record: SavedRecord) => void;
}) {
  type Asking = { rows: ConflictRow[]; version: number; record: SavedRecord };
  const [asking, setAsking] = useState<Asking | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const show = (key: keyof Draft & string, value: unknown): string => {
    const formatter = options.format?.[key] as ((input: unknown) => string) | undefined;
    if (formatter) return formatter(value);
    if (value === null || value === undefined || value === '') return 'Vacío';
    if (typeof value === 'boolean') return value ? 'Sí' : 'No';
    if (Array.isArray(value)) return value.length ? value.join(', ') : 'Ninguno';
    return String(value);
  };

  function handle(error: unknown, drafts: { base: Draft; mine: Draft }): boolean {
    if (!isVersionConflict(error)) return false;
    const record = options.parse(error.conflict.current);
    if (record === null) return false;
    const theirs = options.toDraft(record);
    const plan = planVersionConflict({ base: drafts.base, mine: drafts.mine, theirs, keys: options.keys });
    const merged = { ...theirs, ...plan.patch } as Draft;
    const version = error.conflict.currentVersion;
    options.rebase({ draft: merged, record });
    if (plan.kind === 'ask') {
      setMessage(null);
      setAsking({
        rows: plan.clashes.map((clash) => ({
          label: options.labels[clash.key] ?? clash.key,
          mine: show(clash.key, clash.mine),
          theirs: show(clash.key, clash.theirs),
        })),
        version,
        record,
      });
      return true;
    }
    setAsking(null);
    if (Object.keys(plan.patch).length === 0) {
      options.discard(record);
      setMessage('Esto ya estaba guardado así.');
      return true;
    }
    setMessage('Otra persona cambió otra parte mientras editabas; quedaron los dos cambios.');
    options.save({ draft: merged, version });
    return true;
  }

  function clear() {
    setAsking(null);
    setMessage(null);
  }

  const notice: ReactNode = asking ? (
    <ConflictNotice
      rows={asking.rows}
      onKeepMine={() => {
        const draft = options.current();
        if (draft === null) return;
        setAsking(null);
        options.save({ draft, version: asking.version });
      }}
      onTakeTheirs={() => {
        setAsking(null);
        options.discard(asking.record);
      }}
    />
  ) : message ? (
    <p role="status" className="notice conflict-merged">
      {message}
    </p>
  ) : null;

  return { handle, notice, clear, asking: asking !== null };
}
